import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import intercomService from '../../services/intercom.service';
import { getJwtToken } from '../../services/jwt.services';
import jwt_decode from 'jwt-decode';
import Jiraintercomdescription from '../../components/jira/jiraintercomdescription';
import styles from './Intercom.module.css';

const formatTimestamp = (timestamp) => {
	if (!timestamp) {
		return '';
	}
	const date = new Date(timestamp * 1000);
	if (Number.isNaN(date.getTime())) {
		return '';
	}
	return date.toLocaleString(undefined, {
		year: 'numeric',
		month: 'short',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		timeZoneName: 'short',
	});
};

const formatElapsed = (currentTimestamp, previousTimestamp) => {
	if (!currentTimestamp || !previousTimestamp) {
		return '';
	}
	const deltaSeconds = Math.max(0, currentTimestamp - previousTimestamp);
	const hours = Math.floor(deltaSeconds / 3600);
	const minutes = Math.floor((deltaSeconds % 3600) / 60);
	const seconds = deltaSeconds % 60;
	if (hours > 0) {
		return `${hours}h ${minutes}m`;
	}
	if (minutes > 0) {
		return `${minutes}m ${seconds}s`;
	}
	return `${seconds}s`;
};

const getAuthorLabel = (author) => {
	if (!author) {
		return 'Unknown';
	}
	return author.name || author.email || author.id || 'Unknown';
};

const formatAttributeLabel = (key) => {
	if (!key) {
		return '';
	}
	const normalized = String(key).replace(/^_+|_+$/g, '');
	const words = normalized
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.split(/[\s_]+/)
		.filter(Boolean)
		.map((word) => {
			const lower = word.toLowerCase();
			return lower.charAt(0).toUpperCase() + lower.slice(1);
		});
	return words.join(' ');
};

const formatSecondsAsDuration = (secondsValue) => {
	const totalSeconds = Number(secondsValue);
	if (!Number.isFinite(totalSeconds)) {
		return String(secondsValue);
	}

	const normalizedSeconds = Math.max(0, Math.round(totalSeconds));
	const days = Math.floor(normalizedSeconds / 86400);
	const hours = Math.floor((normalizedSeconds % 86400) / 3600);
	const minutes = Math.floor((normalizedSeconds % 3600) / 60);
	const seconds = normalizedSeconds % 60;

	const parts = [];
	if (days > 0) {
		parts.push(`${days}d`);
	}
	if (hours > 0 || days > 0) {
		parts.push(`${hours}h`);
	}
	if (minutes > 0 || hours > 0 || days > 0) {
		parts.push(`${minutes}m`);
	}
	parts.push(`${seconds}s`);

	return `${parts.join(' ')} (${normalizedSeconds.toLocaleString()}s)`;
};

const formatMillisecondsAsDuration = (millisecondsValue) => {
	const totalMilliseconds = Number(millisecondsValue);
	if (!Number.isFinite(totalMilliseconds)) {
		return String(millisecondsValue);
	}
	const normalizedMilliseconds = Math.max(0, Math.round(totalMilliseconds));
	const seconds = normalizedMilliseconds / 1000;
	return `${formatSecondsAsDuration(seconds).replace(/\(\d[\d,]*s\)$/, '').trim()} (${normalizedMilliseconds.toLocaleString()}ms)`;
};

const formatEpochTime = (timestamp, unit) => {
	const milliseconds = unit === 'ms' ? timestamp : timestamp * 1000;
	const date = new Date(milliseconds);
	if (Number.isNaN(date.getTime())) {
		return null;
	}
	return date.toLocaleString(undefined, {
		year: 'numeric',
		month: 'short',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		timeZoneName: 'short',
	});
};

const formatTimeLikeNumber = (key, rawValue) => {
	const keyLower = String(key || '').toLowerCase();
	const numericValue = Number(rawValue);
	if (!Number.isFinite(numericValue)) {
		return null;
	}

	const isEpochKey = /(date|timestamp|_at)$/.test(keyLower) || keyLower.includes('created') || keyLower.includes('updated');
	if (isEpochKey) {
		if (numericValue >= 1000000000000) {
			return formatEpochTime(numericValue, 'ms');
		}
		if (numericValue >= 1000000000) {
			return formatEpochTime(numericValue, 's');
		}
	}

	const isMilliseconds = keyLower.includes('millisecond') || /(^|[_\s])ms($|[_\s])/.test(keyLower);
	if (isMilliseconds) {
		return `${formatMillisecondsAsDuration(numericValue)} (milliseconds)`;
	}

	const isSeconds =
		keyLower.includes('response_time') ||
		keyLower.includes('response time') ||
		keyLower.includes('duration') ||
		keyLower.includes('latency') ||
		keyLower.includes('elapsed') ||
		keyLower.includes('seconds') ||
		keyLower.endsWith('_s');
	if (isSeconds) {
		return `${formatSecondsAsDuration(numericValue)} (seconds)`;
	}

	const looksLikeGenericTime = keyLower.includes('time');
	if (looksLikeGenericTime && Number.isFinite(numericValue)) {
		return numericValue.toLocaleString();
	}

	return null;
};

const isIdLikeKey = (key) => {
	const keyLower = String(key || '').toLowerCase();
	return keyLower === 'id' || keyLower.endsWith('_id') || keyLower.includes(' id');
};

const getFormattedStructuredValue = (value, parentKey = '') => {
	if (Array.isArray(value)) {
		return value.map((item) => getFormattedStructuredValue(item, parentKey));
	}
	if (value && typeof value === 'object') {
		return Object.entries(value).reduce((acc, [key, nestedValue]) => {
			acc[key] = getFormattedStructuredValue(nestedValue, key);
			return acc;
		}, {});
	}
	if (typeof value === 'number') {
		const formattedTimeValue = formatTimeLikeNumber(parentKey, value);
		if (formattedTimeValue) {
			return formattedTimeValue;
		}
		if (isIdLikeKey(parentKey)) {
			return String(value);
		}
		return value;
	}
	if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) {
		const formattedTimeValue = formatTimeLikeNumber(parentKey, Number(value));
		if (formattedTimeValue) {
			return formattedTimeValue;
		}
	}
	return value;
};

const toPrettyJson = (value, parentKey = '') => {
	try {
		return JSON.stringify(getFormattedStructuredValue(value, parentKey), null, 2);
	} catch (error) {
		return String(value);
	}
};

const formatAttributeValue = (key, value) => {
	if (value === null || value === undefined) {
		return 'Not available';
	}

	const keyLower = String(key || '').toLowerCase();

	if (typeof value === 'boolean') {
		return value ? 'Yes' : 'No';
	}

	if (typeof value === 'number') {
		const formattedTimeValue = formatTimeLikeNumber(key, value);
		if (formattedTimeValue) {
			return formattedTimeValue;
		}
		if (isIdLikeKey(key)) {
			return String(value);
		}
		return Number.isFinite(value) ? value.toLocaleString() : String(value);
	}

	if (typeof value === 'string') {
		const normalized = value.trim();
		if (!normalized) {
			return 'Not available';
		}
		const lower = normalized.toLowerCase();
		if (lower === 'true') {
			return 'Yes';
		}
		if (lower === 'false') {
			return 'No';
		}
		if (/^-?\d+(\.\d+)?$/.test(normalized)) {
			const formattedTimeValue = formatTimeLikeNumber(key, Number(normalized));
			if (formattedTimeValue) {
				return formattedTimeValue;
			}
		}
		const looksLikeDateKey = /(date|time|timestamp|_at)$/.test(keyLower) || keyLower.includes('created') || keyLower.includes('updated');
		if (looksLikeDateKey) {
			const parsed = Date.parse(normalized);
			if (!Number.isNaN(parsed)) {
				return new Date(parsed).toLocaleString(undefined, {
					year: 'numeric',
					month: 'short',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					timeZoneName: 'short',
				});
			}
		}
		return normalized;
	}

	return toPrettyJson(value, key);
};

const getSortedEntries = (obj) => Object.entries(obj || {}).sort(([a], [b]) => (
	formatAttributeLabel(a).localeCompare(formatAttributeLabel(b))
));

const isDescriptionAttribute = (key) => {
	const keyLower = String(key || '').toLowerCase().replace(/^_+|_+$/g, '');
	return keyLower === 'default_description' || keyLower.endsWith('description');
};

const BOT_FIELD_PREFIX = '🤖';

const isBotFieldKey = (key) => String(key || '').trimStart().startsWith(BOT_FIELD_PREFIX);

const formatBotFieldLabel = (key) => formatAttributeLabel(String(key || '').replace(BOT_FIELD_PREFIX, ''));

// Field definitions keyed by their normalized label (emoji stripped, title-cased).
// `type`: 'select' renders a dropdown, 'short' a single-line input, 'long' a multiline textarea.
const BOT_FIELD_DEFS = {
	Application: {
		type: 'select',
		options: ['MI', 'Klear', 'Radarly'],
		help: "Restricts the Tracker to a specific product brand. Mel filters by this after confirming the customer's product context from their account PLIs.",
	},
	Visibility: {
		type: 'select',
		options: ['None', 'Meltwater', 'External'],
		help: "Controls which conversations Mel may surface this Tracker in. 'External' is shared with customers (non-sales). 'Meltwater' is for internal conversations only (e.g. sales teams). 'None' disables visibility.",
	},
	'Doc Status': {
		type: 'select',
		options: ['None', 'Draft', 'Published', 'Off'],
		help: "Tracks the content lifecycle. None = fields not yet populated. Draft = content added, awaiting review. Published = active and visible to Mel. Off = intentionally disabled. Only 'Published' Trackers are read by Mel.",
	},
	Summary: {
		type: 'long',
		help: 'One generic sentence describing the problem. Mel uses this during the title scan step to identify candidate matches.',
	},
	Detail: {
		type: 'long',
		help: 'Full generic description of the problem, steps to reproduce, and what goes wrong. Uses square-bracket references for customer-specific values (e.g. [Search Name], [Chart Type]).',
	},
	Handling: {
		type: 'long',
		help: 'Instructions for Mel once a match is confirmed: workaround steps, whether to link the ticket, and estimated fix timeframe if known. Leave blank if no workaround exists.',
	},
};

// Unconfigured bot fields default to a date editor when the label looks date-like,
// otherwise a multiline text editor (e.g. Meta).
const getBotFieldDef = (key) => {
	const configured = BOT_FIELD_DEFS[formatBotFieldLabel(key)];
	if (configured) {
		return configured;
	}
	if (/date$/.test(formatBotFieldLabel(key).toLowerCase())) {
		return { type: 'date', help: '' };
	}
	return { type: 'long', help: '' };
};

// Bot date fields are stored as epoch seconds; convert to/from the YYYY-MM-DD the
// native date input expects, preserving the epoch format on save.
const epochToDateInputValue = (raw) => {
	const num = Number(raw);
	if (!Number.isFinite(num) || num === 0) {
		return '';
	}
	const date = new Date(num >= 1e12 ? num : num * 1000);
	if (Number.isNaN(date.getTime())) {
		return '';
	}
	const pad = (value) => String(value).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const dateInputValueToEpoch = (value) => {
	if (!value) {
		return '';
	}
	const ms = new Date(`${value}T00:00:00`).getTime();
	return Number.isNaN(ms) ? '' : String(Math.floor(ms / 1000));
};

const isResponseTimeMetric = (key) => {
	const keyLower = String(key || '').toLowerCase();
	return keyLower.includes('response_time') || keyLower.includes('response time');
};

const isFeaturedResponseMetric = (key) => {
	const keyLower = String(key || '').toLowerCase();
	return (
		keyLower.includes('assigned_team_first_response_time') ||
		keyLower.includes('assigned team first response time')
	);
};

const conversationHasTicket = (conversation) => {
	if (!conversation) {
		return false;
	}
	if (conversation.ticket || conversation.ticket_id || conversation.ticketId) {
		return true;
	}
	if (Array.isArray(conversation.tickets) && conversation.tickets.length > 0) {
		return true;
	}
	const linkedTicket = conversation?.linked_objects?.data?.find((item) => item?.type === 'ticket');
	return Boolean(linkedTicket);
};

const IntercomConversationDetail = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const [conversation, setConversation] = useState(null);
	const [ticket, setTicket] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState('');
	const [isTicketLoading, setIsTicketLoading] = useState(false);
	const [ticketErrorMessage, setTicketErrorMessage] = useState('');
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [isEscalating, setIsEscalating] = useState(false);
	const [escalateMessage, setEscalateMessage] = useState('');
	const [escalateError, setEscalateError] = useState('');
	const [isEscalateDialogOpen, setIsEscalateDialogOpen] = useState(false);
	const [escalateReason, setEscalateReason] = useState('');
	const [escalateDialogError, setEscalateDialogError] = useState('');
	const [ticketAttributesOpen, setTicketAttributesOpen] = useState(false);
	const [botFieldsOpen, setBotFieldsOpen] = useState(false);
	const [botFieldValues, setBotFieldValues] = useState({});
	const [isUpdatingBotFields, setIsUpdatingBotFields] = useState(false);
	const [botFieldsMessage, setBotFieldsMessage] = useState('');
	const [botFieldsError, setBotFieldsError] = useState('');
	const [isSummarizing, setIsSummarizing] = useState(false);
	const [statisticsOpen, setStatisticsOpen] = useState(false);
	const [intercomPermissions, setIntercomPermissions] = useState([]);

	const handleBack = () => {
		if (window.history.length > 1) {
			navigate(-1);
		} else {
			navigate('/intercom/conversations');
		}
	};

	useEffect(() => {
		document.title = 'Intercom Conversation - GSD Support Admin';
		return () => {
			document.title = 'GSD Support Admin';
		};
	}, []);

	useEffect(() => {
		const jwtToken = getJwtToken();
		if (!jwtToken) {
			return;
		}
		const decoded = jwt_decode(jwtToken);
		const permissionValue = decoded?.intercomPermissions ?? decoded?.intercompermissions ?? [];
		setIntercomPermissions(permissionValue || []);
	}, []);

	const loadTicket = useCallback(async () => {
		if (!id) {
			return;
		}
		setIsTicketLoading(true);
		setTicketErrorMessage('');
		try {
			const data = await intercomService.getTicketById(id);
			setTicket(data?.result || null);
		} catch (error) {
			setTicketErrorMessage(error?.message || 'Failed to load ticket.');
		} finally {
			setIsTicketLoading(false);
		}
	}, [id]);

	const handleSummarizeBotFields = useCallback(async () => {
		if (!id) {
			return;
		}
		setIsSummarizing(true);
		setBotFieldsError('');
		setBotFieldsMessage('');
		try {
			const data = await intercomService.summarizeBotFields(id);
			const drafted = data?.result || data?.fields || data || {};
			setBotFieldValues((prev) => {
				const next = { ...prev };
				Object.entries(drafted).forEach(([key, value]) => {
					if (key in next) {
						next[key] = value == null ? '' : String(value);
					}
				});
				return next;
			});
			setBotFieldsMessage('AI draft generated. Review the fields, then click Update to save.');
		} catch (error) {
			setBotFieldsError(error?.message || 'Failed to summarize using AI.');
		} finally {
			setIsSummarizing(false);
		}
	}, [id]);

	const handleUpdateBotFields = useCallback(async () => {
		if (!id) {
			return;
		}
		setIsUpdatingBotFields(true);
		setBotFieldsError('');
		setBotFieldsMessage('');
		try {
			await intercomService.updateTicketAttributes(id, botFieldValues);
			setBotFieldsMessage('BOT fields updated successfully.');
			loadTicket();
		} catch (error) {
			setBotFieldsError(error?.message || 'Failed to update BOT fields.');
		} finally {
			setIsUpdatingBotFields(false);
		}
	}, [id, botFieldValues, loadTicket]);

	useEffect(() => {
		const loadConversation = async () => {
			setIsLoading(true);
			setErrorMessage('');
			setTicket(null);
			setTicketErrorMessage('');
			try {
				const data = await intercomService.getConversationById(id);
				const result = data?.result || null;
				setConversation(result);
				if (conversationHasTicket(result)) {
					loadTicket();
				}
			} catch (error) {
				setErrorMessage(error?.message || 'Failed to load conversation.');
			} finally {
				setIsLoading(false);
			}
		};

		if (id) {
			loadConversation();
		}
	}, [id, loadTicket]);

	const messages = useMemo(() => {
		if (!conversation) {
			return [];
		}

		const items = [];
		if (conversation?.source?.body) {
			items.push({
				id: `source-${conversation.source.id || conversation.id}`,
				body: conversation.source.body,
				created_at: conversation.created_at,
				author: conversation.source.author,
				part_type: conversation.source.type,
			});
		}

		const parts = conversation?.conversation_parts?.conversation_parts || [];
		parts.forEach((part) => {
			if (!part?.body) {
				return;
			}
			items.push({
				id: part.id,
				body: part.body,
				created_at: part.created_at,
				author: part.author,
				part_type: part.part_type,
			});
		});

		return items.sort((a, b) => (a.created_at || 0) - (b.created_at || 0));
	}, [conversation]);

	const hasTicket = conversationHasTicket(conversation);
	const intercomFeedUrl = id
		? `https://app.intercom.com/a/apps/nrrln6l2/conversations/${id}?redirectTo=feed`
		: '';

	const isEscalated = useMemo(() => {
		const parts = ticket?.ticket_parts?.ticket_parts || [];
		return parts.some((part) => {
			if (!part?.body) {
				return false;
			}
			return part.body.toLowerCase().includes('gsd_escalate');
		});
	}, [ticket]);

	const isTicketOpen = ticket?.open === true || ticket?.open === 'true';
	const ticketStatus = ticket?.ticket_state?.external_label || ticket?.ticket_state?.category || (ticket ? (isTicketOpen ? 'Open' : 'Closed') : '');
	const ticketNumber = ticket?.ticket_id || ticket?.id || '';
	const ticketTitle = ticket?.ticket_attributes?._default_title_ || 'unknown';
	const tritonTicketNumbers = useMemo(() => {
		const title = typeof ticketTitle === 'string' ? ticketTitle : '';
		const matches = title.match(/TRITON-\d+/gi) || [];
		return Array.from(new Set(matches.map((match) => match.toUpperCase())));
	}, [ticketTitle]);
	const trackerConversationIds = useMemo(() => {
		const linkedObjects = ticket?.linkedobjects?.data || ticket?.linked_objects?.data || [];
		if (!Array.isArray(linkedObjects)) {
			return [];
		}
		const ids = linkedObjects
			.map((item) => item?.id)
			.filter((value) => value !== undefined && value !== null)
			.map((value) => String(value).trim())
			.filter(Boolean);
		return Array.from(new Set(ids));
	}, [ticket]);
	const hasTicketAttributes = Boolean(ticket?.ticket_attributes && Object.keys(ticket.ticket_attributes).length > 0);
	const botFields = useMemo(
		() => getSortedEntries(ticket?.ticket_attributes).filter(([key]) => isBotFieldKey(key)),
		[ticket?.ticket_attributes]
	);
	const hasBotFields = botFields.length > 0;

	useEffect(() => {
		setBotFieldValues(
			Object.fromEntries(botFields.map(([key, value]) => [key, value == null ? '' : String(value)]))
		);
	}, [botFields]);
	const conversationAttributes = useMemo(
		() => conversation?.custom_attributes || {},
		[conversation?.custom_attributes]
	);
	const companyId = useMemo(() => {
		if (!conversationAttributes || typeof conversationAttributes !== 'object') {
			return '';
		}

		const directMatch = conversationAttributes['🪪 Company ID'];
		if (directMatch !== undefined && directMatch !== null && String(directMatch).trim()) {
			return String(directMatch).trim();
		}

		const normalizedTarget = 'companyid';
		const fallbackEntry = Object.entries(conversationAttributes).find(([key]) => {
			const normalizedKey = String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '');
			return normalizedKey === normalizedTarget;
		});
		const fallbackValue = fallbackEntry?.[1];
		if (fallbackValue === undefined || fallbackValue === null) {
			return '';
		}
		return String(fallbackValue).trim();
	}, [conversationAttributes]);
	const miCompanyLoginUrl = companyId
		? `https://app.meltwater.com/idp/tempLogin/${encodeURIComponent(companyId)}`
		: '';
	const hasUsefulLinks = Boolean(id || miCompanyLoginUrl || tritonTicketNumbers.length > 0 || trackerConversationIds.length > 0);
	const canViewLinkedTicketDetails = Array.isArray(intercomPermissions)
		? intercomPermissions.includes('write')
		: String(intercomPermissions || '').includes('write');
	const conversationStatistics = useMemo(
		() => conversation?.statistics || {},
		[conversation?.statistics]
	);
	const conversationAttributeCount = Object.keys(conversationAttributes).length;
	const conversationStatisticsCount = Object.keys(conversationStatistics).length;
	const hasConversationAttributes = conversationAttributeCount > 0;
	const hasConversationStatistics = conversationStatisticsCount > 0;

	const handleEscalate = async (reason) => {
		if (!id || isEscalating) {
			return;
		}
		setIsEscalating(true);
		setEscalateMessage('');
		setEscalateError('');
		setEscalateDialogError('');
		try {
			const response = await intercomService.escalateTicket(id, reason);
			setEscalateMessage(response?.message || 'Ticket escalated successfully.');
			setIsEscalateDialogOpen(false);
			setEscalateReason('');
			await loadTicket();
		} catch (error) {
			setEscalateError(error?.message || 'Failed to escalate ticket.');
			setEscalateDialogError(error?.message || 'Failed to escalate ticket.');
		} finally {
			setIsEscalating(false);
		}
	};

	const openEscalateDialog = () => {
		if (isEscalated || isEscalating || !id || !isTicketOpen) {
			return;
		}
		setEscalateMessage('');
		setEscalateError('');
		setEscalateDialogError('');
		setIsEscalateDialogOpen(true);
	};

	const closeEscalateDialog = () => {
		if (isEscalating) {
			return;
		}
		setIsEscalateDialogOpen(false);
		setEscalateDialogError('');
	};

	const submitEscalation = () => {
		const trimmedReason = escalateReason.trim();
		if (!trimmedReason) {
			setEscalateDialogError('Please provide a reason to escalate.');
			return;
		}
		handleEscalate(trimmedReason);
	};

	return (
		<div className={styles.divStyle}>
			<div className={styles.sectionContainer}>
				<div className={styles.sectionHeader}>
					<Tooltip title="Intercom Conversations">
						<button
							type="button"
							className={styles.backButton}
							onClick={handleBack}
						>
							<ArrowBackIcon className={styles.backIcon} />
						</button>
					</Tooltip>
					<div className={styles.sectionHeaderBody}>
						<div className={styles.sectionTitleRow}>
							<h1 className={styles.sectionTitle}>Conversation {id}</h1>
							{conversation?.state && (
								<Chip label={conversation.state} color="primary" size="small" />
							)}
							{ticketStatus && (
								<Chip label={`Ticket: ${ticketStatus}`} color="secondary" size="small" />
							)}
						</div>
						<p className={styles.sectionDescription}>
							Review the conversation thread and metadata.
						</p>
						{conversation && (
							<div className={styles.sectionMetaRow}>
								<div className={styles.sectionMetaInfo}>
									<span>
										From {getAuthorLabel(conversation.source?.author)}
									</span>
									<span>Created {formatTimestamp(conversation.created_at)}</span>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			<Paper className={styles.detailContainer}>
				{isLoading ? (
					<Box className={styles.loadingState}>
						<CircularProgress />
					</Box>
				) : errorMessage ? (
					<Typography color="error">{errorMessage}</Typography>
				) : conversation ? (
					<>
						<Box className={styles.detailHeader}>
							<Box>
								<Typography variant="h6">
									{conversation.title || conversation.source?.subject || 'Untitled conversation'}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{getAuthorLabel(conversation.source?.author)}{conversation.source?.author?.email ? ` (${conversation.source.author.email})` : ''}
								</Typography>
							</Box>
							{hasUsefulLinks && (
								<div className={`${styles.detailUsefulLinks} ${styles.detailUsefulLinksHeader}`}>
									<Typography variant="subtitle2" className={`${styles.detailSubheader} ${styles.usefulLinksTitle}`}>
										Useful Links
									</Typography>
									<div className={styles.detailLinksInline}>
										{id && (
											<div className={styles.detailLinkRow}>
												<a
													href={intercomFeedUrl}
													target="_blank"
													rel="noreferrer"
													className={`${styles.intercomLink} ${styles.detailLinkLabel}`}
												>
													Open in Intercom
												</a>
											</div>
										)}
										{miCompanyLoginUrl && (
											<div className={styles.detailLinkRow}>
												<a
													href={miCompanyLoginUrl}
													target="_blank"
													rel="noreferrer"
													className={`${styles.intercomLink} ${styles.detailLinkLabel}`}
												>
													MI Company login
												</a>
											</div>
										)}
										{tritonTicketNumbers.map((tritonTicketNumber) => (
											<div className={styles.detailLinkRow} key={tritonTicketNumber}>
												<a
													href={`https://meltwater.atlassian.net/browse/${encodeURIComponent(tritonTicketNumber)}`}
													target="_blank"
													rel="noreferrer"
													className={`${styles.intercomLink} ${styles.detailLinkLabel}`}
												>
													{`Open ${tritonTicketNumber}`}
												</a>
											</div>
										))}
										{canViewLinkedTicketDetails && trackerConversationIds.map((trackerConversationId) => (
											<div className={styles.detailLinkRow} key={`tracker-${trackerConversationId}`}>
												<a
													href={`/intercom/conversations/${encodeURIComponent(trackerConversationId)}`}
													target="_blank"
													rel="noreferrer"
													className={`${styles.intercomLink} ${styles.detailLinkLabel}`}
												>
													View linked ticket details
												</a>
											</div>
										))}
									</div>
								</div>
							)}
						</Box>

						<Box className={styles.detailSplitRow}>
							<Box className={styles.detailColumn}>
								<Typography variant="subtitle2" className={styles.detailColumnTitle}>
									Conversation
								</Typography>
								<div className={styles.detailBlock}>
									<div className={styles.detailMeta}>
										<Typography variant="body2">
											Status: {conversation.state || 'unknown'}
										</Typography>
										<Typography variant="body2">Created: {formatTimestamp(conversation.created_at)}</Typography>
										<Typography variant="body2">Updated: {formatTimestamp(conversation.updated_at)}</Typography>
										<Typography variant="body2">
											Author: {getAuthorLabel(conversation.source?.author)}
										</Typography>
										<Typography variant="body2">
											Source: {conversation.source?.type || 'unknown'}
										</Typography>
										{conversation.source?.url && (
											<Typography variant="body2">
												URL: <a href={conversation.source.url} target="_blank" rel="noreferrer">{conversation.source.url}</a>
											</Typography>
										)}
										<Typography variant="body2">
											Has Ticket: {hasTicket ? 'Yes' : 'No'}
										</Typography>
									</div>
								</div>
								<div className={styles.detailActions}>
									<Button
										variant="outlined"
										size="small"
										className={styles.conversationAttributesButton}
										onClick={() => setDetailsOpen(true)}
										disabled={!hasConversationAttributes}
									>
										Conversation Attributes ({conversationAttributeCount})
									</Button>
									<Button
										variant="outlined"
										size="small"
										className={styles.conversationAttributesButton}
										onClick={() => setStatisticsOpen(true)}
										disabled={!hasConversationStatistics}
									>
										Conversation Statistics ({conversationStatisticsCount})
									</Button>
								</div>
							</Box>

							<Box className={`${styles.detailColumn} ${styles.detailColumnRight}`}>
								<Typography variant="subtitle2" className={styles.detailColumnTitle}>
									Ticket
								</Typography>
								{hasTicket ? (
									<>
										<div className={styles.detailBlock}>
											<div className={styles.detailMeta}>
												<Typography variant="body2">
													Ticket Number:{' '}
													<span className={styles.ticketNumberHighlight}>{ticketNumber || id}</span>
												</Typography>
												<Typography variant="body2">
													Ticket Status: {ticketStatus || 'unknown'}
												</Typography>
												<Typography variant="body2">
													Title: {ticketTitle}
												</Typography>
												<Typography variant="body2">
													Type: {ticket?.type || ticket?.ticket_type?.type || 'unknown'}
												</Typography>
												<Typography variant="body2">
													Category: {ticket?.category || ticket?.ticket_type?.category || 'unknown'}
												</Typography>
												<Typography variant="body2">
													Channel: {ticket?.channel || 'unknown'}
												</Typography>
												<Typography variant="body2">
													Ticket Type: {ticket?.ticket_type?.name || 'unknown'}
												</Typography>
											</div>
										</div>
										<Box className={styles.ticketActionRow}>
											<Button
												variant="outlined"
												size="small"
												className={styles.ticketAttributesButton}
												onClick={() => setTicketAttributesOpen(true)}
												disabled={!ticket || !hasTicketAttributes}
											>
												Ticket Attributes
											</Button>
											<Button
												variant="outlined"
												size="small"
												className={styles.ticketAttributesButton}
												onClick={() => {
													setBotFieldsError('');
													setBotFieldsMessage('');
													setBotFieldsOpen(true);
												}}
												disabled={!ticket || !hasBotFields}
											>
												🤖 BOT Fields
											</Button>
											{isTicketOpen && (
												<Button
													variant="contained"
													size="small"
													onClick={openEscalateDialog}
													disabled={isEscalating || !id || isEscalated}
													color={isEscalated ? 'success' : 'primary'}
													sx={
														isEscalated
															? {
																backgroundColor: 'success.main',
																color: 'success.contrastText',
																'&.Mui-disabled': {
																	backgroundColor: 'success.main',
																	color: 'success.contrastText',
																	opacity: 1,
																},
															}
															: undefined
													}
												>
													{isEscalated ? 'Escalated' : isEscalating ? 'Escalating...' : 'Escalate'}
												</Button>
											)}
										</Box>
										{(ticketErrorMessage || isTicketLoading) && (
											<Box className={styles.detailActions}>
												{isTicketLoading ? (
													<Typography variant="body2">Loading ticket...</Typography>
												) : (
													<Typography variant="body2" color="error">
														{ticketErrorMessage}
													</Typography>
												)}
											</Box>
										)}
									</>
								) : (
									<Typography variant="body2" color="text.secondary">
										No ticket linked.
									</Typography>
								)}
							</Box>
						</Box>

						{(escalateMessage || escalateError) && (
							<Box className={styles.detailActions}>
								<Typography sx={{ color: escalateError ? 'error.main' : 'success.main' }}>
									{escalateError || escalateMessage}
								</Typography>
							</Box>
						)}

						<div className={styles.chatContainer}>
							{messages.length === 0 ? (
								<Typography variant="body2" color="text.secondary">
									No conversation messages available.
								</Typography>
							) : (
								messages.map((message, index) => {
									const previousMessage = messages[index - 1];
									const elapsed = formatElapsed(
										message.created_at,
										previousMessage?.created_at
									);
									const isUser = message?.author?.type === 'user';
									const isNote = String(message?.part_type || '').toLowerCase().includes('note');
									return (
										<div
											key={message.id}
											className={`${styles.messageRow} ${isUser ? styles.messageLeft : styles.messageRight}`}
										>
											<div className={`${styles.messageBubble} ${isNote ? styles.messageNote : ''}`}>
												<div className={styles.messageMeta}>
													<span className={styles.messageAuthor}>{getAuthorLabel(message.author)}</span>
													<span className={styles.messageTime}>
														{formatTimestamp(message.created_at)}
														{elapsed ? ` (+${elapsed})` : ''}
													</span>
												</div>
												<div className={styles.messageBody}>
													<Jiraintercomdescription data={message.body} />
												</div>
												{message.part_type && (
													<div className={styles.messageType}>{message.part_type}</div>
												)}
											</div>
										</div>
									);
								})
							)}
						</div>
					</>
				) : (
					<Typography>No conversation data found.</Typography>
				)}
			</Paper>

			<Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Conversation Attributes</DialogTitle>
				<DialogContent dividers>
					{hasConversationAttributes ? (
						<Box className={styles.detailAttributes}>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
								{conversationAttributeCount} {conversationAttributeCount === 1 ? 'attribute' : 'attributes'}
							</Typography>
							{getSortedEntries(conversationAttributes).map(([key, value]) => {
								const formattedValue = formatAttributeValue(key, value);
								const isStructuredValue = typeof value === 'object' && value !== null;
								const rowClassName = `${styles.detailAttributeRow} ${isStructuredValue ? styles.detailAttributeRowStacked : ''}`.trim();
								return (
									<div key={key} className={rowClassName}>
										<span className={styles.detailAttributeLabel}>{formatAttributeLabel(key)}</span>
										{isStructuredValue ? (
											<pre className={styles.detailAttributeJson}>{formattedValue}</pre>
										) : (
											<span className={styles.detailAttributeValue}>{formattedValue}</span>
										)}
									</div>
								);
							})}
						</Box>
					) : (
						<Typography variant="body2" color="text.secondary">
							No conversation attributes available.
						</Typography>
					)}
				</DialogContent>
			</Dialog>

			<Dialog open={statisticsOpen} onClose={() => setStatisticsOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Conversation Statistics</DialogTitle>
				<DialogContent dividers>
					{hasConversationStatistics ? (
						<Box className={styles.detailAttributes}>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
								{conversationStatisticsCount} {conversationStatisticsCount === 1 ? 'metric' : 'metrics'}
							</Typography>
							{getSortedEntries(conversationStatistics).map(([key, value]) => {
								const formattedValue = formatAttributeValue(key, value);
								const isStructuredValue = typeof value === 'object' && value !== null;
								const isResponseMetric = isResponseTimeMetric(key);
								const isFeaturedMetric = isFeaturedResponseMetric(key);
								const rowClassName = `${styles.detailAttributeRow} ${isResponseMetric ? styles.detailAttributeRowMetric : ''} ${isFeaturedMetric ? styles.detailAttributeRowFeatured : ''} ${isStructuredValue ? styles.detailAttributeRowStacked : ''}`.trim();
								return (
									<div key={key} className={rowClassName}>
										<span className={`${styles.detailAttributeLabel} ${isFeaturedMetric ? styles.detailAttributeLabelFeatured : ''}`}>
											{formatAttributeLabel(key)}
										</span>
										{isStructuredValue ? (
											<pre className={styles.detailAttributeJson}>{formattedValue}</pre>
										) : (
											<span className={`${styles.detailAttributeValue} ${isResponseMetric ? styles.detailAttributeValueMetric : ''}`}>
												{formattedValue}
											</span>
										)}
									</div>
								);
							})}
						</Box>
					) : (
						<Typography variant="body2" color="text.secondary">
							No conversation statistics available.
						</Typography>
					)}
				</DialogContent>
			</Dialog>

			<Dialog
				open={ticketAttributesOpen}
				onClose={() => setTicketAttributesOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Ticket Attributes</DialogTitle>
				<DialogContent dividers>
					{ticket?.ticket_attributes && Object.keys(ticket.ticket_attributes).length > 0 ? (
						<Box className={styles.detailAttributes}>
							{getSortedEntries(ticket.ticket_attributes).map(([key, value]) => {
								const formattedValue = formatAttributeValue(key, value);
								const isStructuredValue = typeof value === 'object' && value !== null;
								const isDescription = !isStructuredValue && isDescriptionAttribute(key) && typeof value === 'string' && value.trim();
								const rowClassName = `${styles.detailAttributeRow} ${(isStructuredValue || isDescription) ? styles.detailAttributeRowStacked : ''}`.trim();
								return (
									<div key={key} className={rowClassName}>
										<span className={styles.detailAttributeLabel}>{formatAttributeLabel(key)}</span>
										{isStructuredValue ? (
											<pre className={styles.detailAttributeJson}>{formattedValue}</pre>
										) : isDescription ? (
											<div className={styles.detailAttributeDescription}>
												<Jiraintercomdescription data={value} />
											</div>
										) : (
											<span className={styles.detailAttributeValue}>
												{formattedValue}
											</span>
										)}
									</div>
								);
							})}
						</Box>
					) : (
						<Typography variant="body2" color="text.secondary">
							No ticket attributes available.
						</Typography>
					)}
				</DialogContent>
			</Dialog>

			<Dialog
				open={botFieldsOpen}
				onClose={() => setBotFieldsOpen(false)}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>🤖 BOT Fields</DialogTitle>
				<DialogContent dividers>
					{hasBotFields ? (
						<Box className={styles.botFieldForm}>
							{botFields.map(([key]) => {
								const def = getBotFieldDef(key);
								const isDate = def.type === 'date';
								const fieldValue = botFieldValues[key] ?? '';
								const inputValue = isDate ? epochToDateInputValue(fieldValue) : fieldValue;
								const handleChange = (event) => {
									const nextValue = isDate
										? dateInputValueToEpoch(event.target.value)
										: event.target.value;
									setBotFieldValues((prev) => ({ ...prev, [key]: nextValue }));
								};
								const isCompact = def.type === 'select' || isDate;
								const cellClassName = `${styles.botFieldCell} ${isCompact ? styles.botFieldCellSelect : styles.botFieldCellFull}`.trim();
								return (
									<div key={key} className={cellClassName}>
										<TextField
											label={formatBotFieldLabel(key)}
											value={inputValue}
											onChange={handleChange}
											helperText={def.help}
											select={def.type === 'select'}
											multiline={def.type === 'long'}
											minRows={def.type === 'long' ? 3 : undefined}
											type={isDate ? 'date' : undefined}
											InputLabelProps={isDate ? { shrink: true } : undefined}
											fullWidth
											size="small"
											margin="dense"
										>
											{def.type === 'select'
												? def.options.map((option) => (
													<MenuItem key={option} value={option}>
														{option}
													</MenuItem>
												))
												: null}
										</TextField>
									</div>
								);
							})}
						</Box>
					) : (
						<Typography variant="body2" color="text.secondary">
							No BOT fields available.
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					{(botFieldsError || botFieldsMessage) && (
						<Typography
							variant="body2"
							sx={{ mr: 'auto', ml: 1, color: botFieldsError ? 'error.main' : 'success.main' }}
						>
							{botFieldsError || botFieldsMessage}
						</Typography>
					)}
					<Button onClick={() => setBotFieldsOpen(false)} disabled={isUpdatingBotFields || isSummarizing}>
						Close
					</Button>
					{/* TODO: re-enable once Summarize/Update functionality is implemented */}
					<Button
						variant="outlined"
						onClick={handleSummarizeBotFields}
						disabled
						startIcon={isSummarizing ? <CircularProgress size={16} /> : null}
					>
						{isSummarizing ? 'Summarizing…' : '🤖 Summarize using AI'}
					</Button>
					<Button
						variant="contained"
						onClick={handleUpdateBotFields}
						disabled
						startIcon={isUpdatingBotFields ? <CircularProgress size={16} /> : null}
					>
						{isUpdatingBotFields ? 'Updating…' : 'Update'}
					</Button>
				</DialogActions>
			</Dialog>


			<Dialog
				open={isEscalateDialogOpen}
				onClose={closeEscalateDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Escalate Ticket</DialogTitle>
				<DialogContent dividers>
					<TextField
						label="Reason to escalate"
						required
						value={escalateReason}
						onChange={(event) => {
							setEscalateReason(event.target.value);
							if (escalateDialogError) {
								setEscalateDialogError('');
							}
						}}
						placeholder="Provide a brief reason for escalation"
						fullWidth
						multiline
						minRows={3}
						autoFocus
					/>
					{escalateDialogError && (
						<Typography sx={{ color: 'error.main', mt: 1 }}>
							{escalateDialogError}
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeEscalateDialog} disabled={isEscalating}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={submitEscalation}
						disabled={isEscalating}
					>
						{isEscalating ? 'Escalating...' : 'Escalate'}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default IntercomConversationDetail;
